import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { BookOpen } from "lucide-react";
import ToastContainer, { showToast } from "./Toast";
import { STORAGE_KEYS, readStorage, saveStorage, addActivity, getSchoolSettings, loadModuleData, saveModuleData, addActivityAsync, loadGoals, loadActions } from "./utils";
import StaffSelect from "./StaffSelect";

const TAG = {
  green: "bg-[#eaf8f1] text-[#2d9b68] border-[#cfe8dc]",
  blue: "bg-[#eef6fc] text-[#2d81c1] border-[#cfe3f3]",
  orange: "bg-[#fff4eb] text-[#f47c20] border-[#f7c9a1]",
  yellow: "bg-[#fff7e7] text-[#d58d1f] border-[#f4deab]",
  red: "bg-[#fff0f0] text-[#d95353] border-[#f4c5c5]",
};

function Tag({ cls = "blue", children }) {
  return (
    <span className={`inline-flex items-center gap-[6px] px-[10px] py-[6px] rounded-full text-[11px] font-semibold border whitespace-nowrap ${TAG[cls] || TAG.blue}`}>
      {children}
    </span>
  );
}

function Window({ title, note, headerRight, children }) {
  return (
    <section className="bg-white border border-[#e6ecf3] rounded-[26px] shadow-[0_16px_36px_rgba(19,31,47,.07)] overflow-hidden">
      <div className="px-5 py-[18px] border-b border-[#e6ecf3] flex items-center justify-between gap-3">
        <div>
          <div className="text-[16px] font-semibold text-[#233445] mb-[2px]">{title}</div>
          {note && <div className="text-[12px] text-[#617587]">{note}</div>}
        </div>
        {headerRight && <div className="flex flex-wrap gap-[10px]">{headerRight}</div>}
      </div>
      <div className="p-5">{children}</div>
    </section>
  );
}

const EMPTY_LOG = { goal: "", action: "", reviewDate: "", status: "", progressNote: "", blocker: "", nextReviewDate: "", reviewer: "" };

function statusCls(s) {
  if (s === "On Track") return "green";
  if (s === "Needs Attention") return "orange";
  return "red";
}

function calculateCompletion(reviews) {
  const total = reviews.length;
  const withGoal = reviews.filter(r => r.goal).length;
  const withAction = reviews.filter(r => r.action).length;
  const withRisks = reviews.filter(r => r.blocker).length;
  const withNextReview = reviews.filter(r => r.nextReviewDate).length;
  const onTrack = reviews.filter(r => r.status === "On Track").length;

  let filled = 0;
  if (total > 0) filled++;
  if (total >= 3) filled++;
  if (withGoal >= Math.min(total, 2) && total > 0) filled++;
  if (withAction >= Math.min(total, 2) && total > 0) filled++;
  if (withNextReview > 0) filled++;
  if (withRisks > 0) filled++;
  if (onTrack > 0 || total > 0) filled++;
  if (total >= 5) filled++;

  return Math.round((filled / 8) * 100);
}

export default function Monitoring() {
  const settings = getSchoolSettings();
  const [reviews, setReviews] = useState(() => {
    const stored = readStorage(STORAGE_KEYS.monitoring, null);
    return Array.isArray(stored?.reviews) ? stored.reviews : [];
  });
  const [goals, setGoals] = useState(() => {
    const stored = readStorage(STORAGE_KEYS.goals, null);
    return Array.isArray(stored?.goals) ? stored.goals : [];
  });
  const [actions, setActions] = useState(() => {
    const stored = readStorage(STORAGE_KEYS.actions, null);
    return Array.isArray(stored?.actions) ? stored.actions : [];
  });
  const [form, setForm] = useState(EMPTY_LOG);
  const [editingIndex, setEditingIndex] = useState(null);
  const [activeTab, setActiveTab] = useState("entry");
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [reviewDateFrom, setReviewDateFrom] = useState("");
  const [reviewDateTo, setReviewDateTo] = useState("");

  useEffect(() => {
    loadModuleData("monitoring", null).then(d => {
      if (d?.reviews) setReviews(d.reviews);
    });
    loadGoals([]).then(g => { if (g?.length) setGoals(g); });
    loadActions([]).then(a => { if (a?.length) setActions(a); });
  }, []);

  function persist(list, label = "Monitoring saved") {
    saveModuleData("monitoring", { reviews: list }, calculateCompletion(list));
    addActivityAsync("Monitoring", label, "Saved", "green");
  }

  function handleAdd() {
    if (!form.goal || form.goal === "__no_goals__" || !form.status || !form.progressNote.trim()) {
      showToast("Linked Goal, Review Status, and Progress Note are required.", "error");
      return;
    }
    let updated;
    if (editingIndex !== null) {
      updated = reviews.map((r, i) => i === editingIndex ? form : r);
      persist(updated, "Monitoring log updated");
      showToast("Monitoring log updated.", "success");
    } else {
      updated = [...reviews, form];
      persist(updated, "Monitoring log added");
      showToast("Monitoring log added.", "success");
    }
    setReviews(updated);
    setForm(EMPTY_LOG);
    setEditingIndex(null);
  }

  function handleEdit(i) {
    setForm(reviews[i]);
    setEditingIndex(i);
    setActiveTab("entry");
  }

  function handleDelete(i) {
    const updated = reviews.filter((_, idx) => idx !== i);
    persist(updated, "Monitoring log deleted");
    setReviews(updated);
    showToast("Log deleted.", "warn");
  }

  function handleFillSample() {
    const sample = [
      { goal: "Improve teaching consistency across middle grades", action: "Schedule monthly classroom observations", reviewDate: "2026-05-15", status: "On Track", progressNote: "Three classroom observations completed so far, feedback collected and shared with teachers.", blocker: "Two teachers unavailable during scheduled observation week.", nextReviewDate: "2026-06-15", reviewer: "Academic Coordinator" },
      { goal: "Strengthen parent communication and trust", action: "Design parent communication calendar", reviewDate: "2026-05-10", status: "Needs Attention", progressNote: "Calendar created but parent response rate still low for scheduled updates.", blocker: "SMS communication tool budget approval pending.", nextReviewDate: "2026-06-01", reviewer: "Principal" },
      { goal: "Improve review-to-action follow-through", action: "Create classroom observation rubric", reviewDate: "2026-05-05", status: "On Track", progressNote: "Rubric finalized and approved by principal. Training session for reviewers scheduled.", blocker: "", nextReviewDate: "2026-06-05", reviewer: "Vice Principal" },
    ];
    persist(sample, "Sample monitoring logs loaded");
    setReviews(sample);
    showToast("Sample Monitoring content loaded.", "success");
  }

  function handleClear() {
    if (!window.confirm("This will clear all saved monitoring data. Continue?")) return;
    saveModuleData("monitoring", { reviews: [] }, 0);
    addActivityAsync("Monitoring", "Module cleared", "Cleared", "red");
    setReviews([]);
    setForm(EMPTY_LOG);
    setEditingIndex(null);
    showToast("Monitoring module cleared.", "warn");
  }

  const completion = calculateCompletion(reviews);
  const risks = reviews.filter(r => r.blocker && r.blocker.trim() !== "").length;
  const readiness = completion >= 85 ? "High" : completion >= 55 ? "Medium" : "Low";
  const interpretation = completion >= 85
    ? "The school has a fairly strong monitoring rhythm and can now move into summary reporting with useful evidence."
    : completion >= 55
    ? "The school has a partial monitoring structure, but needs more regular and deeper review logs."
    : "The school still needs more review checkpoints before overall reporting becomes meaningful.";

  const uniqueGoals = [...new Set(reviews.map(r => r.goal).filter(Boolean))];
  const onTrack = reviews.filter(r => r.status === "On Track").length;
  const needsAttention = reviews.filter(r => r.status === "Needs Attention").length;
  const offTrack = reviews.filter(r => r.status === "Off Track").length;
  const withNextReview = reviews.filter(r => r.nextReviewDate).length;

  const MONITORING_STATUS_OPTIONS = ["On Track","Needs Attention","Off Track"];

  const filtered = reviews.filter(r =>
    (!searchQuery.trim() ||
      (r.goal || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
      (r.action || "").toLowerCase().includes(searchQuery.toLowerCase())) &&
    (!statusFilter || r.status === statusFilter) &&
    (!reviewDateFrom || (r.reviewDate && r.reviewDate >= reviewDateFrom)) &&
    (!reviewDateTo || (r.reviewDate && r.reviewDate <= reviewDateTo))
  );

  const tabBtn = (tab, label) => (
    <button
      onClick={() => setActiveTab(tab)}
      className={`px-5 py-[10px] rounded-[12px] text-[13px] font-semibold transition-colors ${
        activeTab === tab ? "bg-[#f47c20] text-white" : "bg-white text-[#617587] border border-[#e6ecf3] hover:bg-[#eef6fc] hover:text-[#2d81c1]"
      }`}
    >
      {label}
    </button>
  );

  return (

<>
      <div className="flex-1 min-w-0 flex flex-col border-l-2 border-slate-200 ml-1">

        <div className="flex justify-center pl-10 pr-6 py-6">
          <main className="w-full font-[poppins]">
            <section className="mb-8">
              <div className="text-center px-4 py-6">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-6 bg-[#D67C2C]">
                  <BookOpen className="w-8 h-8 text-white" />
                </div>
                <h1 className="text-5xl font-bold text-[#D67C2C] mb-4">Monitoring</h1>
                <p className="text-[#1D3469] text-[18px] sm:text-[20px] font-medium max-w-5xl mx-auto px-4 leading-[1.4]">
                  Track progress, blockers, and review checkpoints to keep execution visible and accountable.
                </p>
              </div>
            </section>

            <div className="mb-[18px] px-5 py-4 rounded-[16px] bg-[#eef6fc] border border-[#cfe3f3]">
              <div className="text-[13px] text-[#617587] max-w-[980px]">
                Monitoring entries should capture real progress, risks, and the next review step for every priority execution stream.
              </div>
            </div>

            <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              {[
                { label: "Module Completion", icon: "📌", iconCls: "bg-[#fff4eb] text-[#f47c20] border-[#f7c9a1]", value: `${completion}%`, meta: "Based on review logs and monitoring depth", bar: true },
                { label: "Monitoring Logs", icon: "📝", iconCls: "bg-[#eef6fc] text-[#2d81c1] border-[#cfe3f3]", value: reviews.length, meta: "Checkpoint entries currently recorded" },
                { label: "Risk / Blocker Logs", icon: "⛔", iconCls: "bg-[#fff0f0] text-[#d95353] border-[#f4c5c5]", value: risks, meta: "Entries carrying blockers or risks" },
                { label: "Monitoring Readiness", icon: "📍", iconCls: "bg-[#fff7e7] text-[#d58d1f] border-[#f4deab]", value: readiness, meta: "How ready the school is for reporting" },
              ].map((s, i) => (
                <div key={i} className="bg-white rounded-xl shadow-md border border-gray-200 p-6 hover:shadow-lg transition-all duration-300 min-h-[130px] flex flex-col justify-between">
                  <div className="flex items-center justify-between gap-[10px] mb-3">
                    <span className="text-[12px] text-[#617587] font-medium">{s.label}</span>
                    <div className={`w-[42px] h-[42px] rounded-[14px] grid place-items-center text-[18px] border ${s.iconCls}`}>{s.icon}</div>
                  </div>
                  <div className="text-[28px] font-bold leading-none text-[#233445] mb-[6px]">{s.value}</div>
                  <div className="text-[12px] text-[#617587]">{s.meta}</div>
                  {s.bar && <div className="w-full h-[6px] rounded-full bg-[#edf2f8] overflow-hidden mt-2"><div className="h-full rounded-full bg-gradient-to-r from-[#f47c20] to-[#2d81c1]" style={{ width: `${completion}%` }} /></div>}
                </div>
              ))}
            </section>

            <div className="flex gap-3 mb-[18px]">
              {tabBtn("entry", "Add Monitoring Log")}
              {tabBtn("logs", "Monitoring Logs")}
              {tabBtn("preview", "Live Preview")}
            </div>

            {activeTab === "entry" && (
              <div className="grid gap-[18px]" style={{ gridTemplateColumns: "1.2fr .8fr" }}>
                <Window title="Monitoring Entry" note="Add one review checkpoint at a time." headerRight={
                  <>
                    <button onClick={() => { setForm(EMPTY_LOG); setEditingIndex(null); }} className="min-h-[44px] px-4 bg-white text-[#233445] border border-[#e6ecf3] rounded-[12px] text-[13px] font-semibold hover:bg-[#eef6fc] hover:text-[#2d81c1] hover:border-[#cfe3f3] transition-colors">Reset Input</button>
                    <button onClick={handleAdd} className="min-h-[44px] px-4 bg-[#f47c20] text-white rounded-[12px] text-[13px] font-semibold hover:bg-[#e46c13] transition-colors">{editingIndex !== null ? "Update Log" : "Add Monitoring Log"}</button>
                  </>
                }>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="flex flex-col gap-1">
                      <label className="text-[12px] font-semibold text-[#233445]">Linked Goal</label>
                      <select value={form.goal} onChange={e => setForm(f => ({ ...f, goal: e.target.value }))} className="h-[42px] border border-[#e6ecf3] rounded-[10px] px-3 text-[13px] text-[#233445] bg-white outline-none focus:border-[#2d81c1]">
                        <option value="">Select linked goal</option>
                        {!goals.length ? <option value="__no_goals__">No goals found — add goals first</option> : goals.map((g, i) => { const label = g.title || g.goal || ""; return <option key={i} value={label}>{label}</option>; })}
                      </select>
                    </div>
                    <div className="flex flex-col gap-1">
                      <label className="text-[12px] font-semibold text-[#233445]">Linked Action Item</label>
                      <select value={form.action} onChange={e => setForm(f => ({ ...f, action: e.target.value }))} className="h-[42px] border border-[#e6ecf3] rounded-[10px] px-3 text-[13px] text-[#233445] bg-white outline-none focus:border-[#2d81c1]">
                        <option value="">Select linked action item</option>
                        {!actions.length ? <option value="__no_actions__">No action items found — add action plans first</option> : actions.map((a, i) => <option key={i} value={a.title || ""}>{a.title || ""}</option>)}
                      </select>
                    </div>
                    <div className="flex flex-col gap-1">
                      <label className="text-[12px] font-semibold text-[#233445]">Review Date</label>
                      <input type="date" value={form.reviewDate} onChange={e => setForm(f => ({ ...f, reviewDate: e.target.value }))} className="h-[42px] border border-[#e6ecf3] rounded-[10px] px-3 text-[13px] text-[#233445] bg-white outline-none focus:border-[#2d81c1]" />
                    </div>
                    <div className="flex flex-col gap-1">
                      <label className="text-[12px] font-semibold text-[#233445]">Review Status</label>
                      <select value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))} className="h-[42px] border border-[#e6ecf3] rounded-[10px] px-3 text-[13px] text-[#233445] bg-white outline-none focus:border-[#2d81c1]">
                        <option value="">Select status</option>
                        {["On Track","Needs Attention","Off Track"].map(s => <option key={s}>{s}</option>)}
                      </select>
                    </div>
                    <div className="flex flex-col gap-1 col-span-2">
                      <label className="text-[12px] font-semibold text-[#233445]">Progress Note</label>
                      <textarea value={form.progressNote} onChange={e => setForm(f => ({ ...f, progressNote: e.target.value }))} placeholder="What progress has happened since the last checkpoint?" rows={3} className="border border-[#e6ecf3] rounded-[10px] px-3 py-2 text-[13px] text-[#233445] bg-white outline-none focus:border-[#2d81c1] resize-y" />
                    </div>
                    <div className="flex flex-col gap-1 col-span-2">
                      <label className="text-[12px] font-semibold text-[#233445]">Blocker / Risk</label>
                      <textarea value={form.blocker} onChange={e => setForm(f => ({ ...f, blocker: e.target.value }))} placeholder="What is slowing or blocking progress, if anything?" rows={2} className="border border-[#e6ecf3] rounded-[10px] px-3 py-2 text-[13px] text-[#233445] bg-white outline-none focus:border-[#2d81c1] resize-y" />
                    </div>
                    <div className="flex flex-col gap-1">
                      <label className="text-[12px] font-semibold text-[#233445]">Next Review Date</label>
                      <input type="date" value={form.nextReviewDate} onChange={e => setForm(f => ({ ...f, nextReviewDate: e.target.value }))} className="h-[42px] border border-[#e6ecf3] rounded-[10px] px-3 text-[13px] text-[#233445] bg-white outline-none focus:border-[#2d81c1]" />
                    </div>
                    <div className="flex flex-col gap-1">
                      <label className="text-[12px] font-semibold text-[#233445]">Reviewer Name</label>
                      <StaffSelect value={form.reviewer} onChange={v => setForm(f => ({ ...f, reviewer: v }))} placeholder="Search staff or type a name…" />
                    </div>
                  </div>
                </Window>

                <Window title="Why monitoring matters" note="How this page keeps execution alive.">
                  {[
                    { title: "Shows whether plans are moving or not", text: "A school may have good plans on paper, but this page reveals whether work is actually happening over time." },
                    { title: "Captures blockers early", text: "Monitoring makes it easier to identify risks, slippages, and obstacles before they become bigger execution failures." },
                    { title: "Builds review discipline", text: "This module encourages regular review cycles instead of one-time planning discussions." },
                    { title: "Feeds final reporting", text: "The next reporting module will summarize overall execution status based on these monitoring logs." },
                  ].map((info, i) => (
                    <div key={i} className="border border-[#e6ecf3] bg-[#fbfcfe] rounded-[14px] p-4 mb-3 last:mb-0">
                      <div className="text-[14px] font-semibold text-[#233445] mb-[6px]">{info.title}</div>
                      <div className="text-[12px] text-[#617587] leading-[1.7]">{info.text}</div>
                    </div>
                  ))}
                </Window>
              </div>
            )}

            {activeTab === "logs" && (
              <div className="grid grid-cols-2 gap-[18px]">
                <Window title="Checkpoint Cards" note="Quick visual view of all monitoring entries.">
                  {!reviews.length ? (
                    <div className="text-center py-6 text-[13px] text-[#617587]">No monitoring logs added yet.</div>
                  ) : (
                    <div className="flex flex-col gap-3">
                      {reviews.map((item, i) => (
                        <div key={i} className="border border-[#e6ecf3] bg-[#fbfcfe] rounded-[14px] p-4">
                          <div className="flex justify-between gap-[10px] items-start mb-2">
                            <h4 className="text-[14px] font-semibold text-[#233445]">{item.action || item.goal || "Monitoring Entry"}</h4>
                            <Tag cls={statusCls(item.status)}>{item.status}</Tag>
                          </div>
                          <p className="text-[12px] text-[#617587] mb-3">{item.progressNote}</p>
                          <div className="flex gap-2 flex-wrap">
                            <Tag cls="blue">{item.goal || "No Goal"}</Tag>
                            {item.reviewDate && <Tag cls="green">{item.reviewDate}</Tag>}
                            {item.blocker && <Tag cls="red">Has Blocker</Tag>}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </Window>

                <Window title="Monitoring Table" note="Editable log table for all checkpoint entries.">
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
                    <div className="flex flex-col gap-1">
                      <label className="text-[11px] font-semibold text-[#617587]">Search</label>
                      <input value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search goal or action…" className="h-[38px] border border-[#e6ecf3] rounded-[10px] px-3 text-[12px] text-[#233445] bg-white outline-none focus:border-[#2d81c1]" />
                    </div>
                    <div className="flex flex-col gap-1">
                      <label className="text-[11px] font-semibold text-[#617587]">Status</label>
                      <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="h-[38px] border border-[#e6ecf3] rounded-[10px] px-3 text-[12px] text-[#233445] bg-white outline-none focus:border-[#2d81c1]">
                        <option value="">All Statuses</option>
                        {MONITORING_STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </div>
                    <div className="flex flex-col gap-1">
                      <label className="text-[11px] font-semibold text-[#617587]">Review Date From</label>
                      <input type="date" value={reviewDateFrom} onChange={e => setReviewDateFrom(e.target.value)} className="h-[38px] border border-[#e6ecf3] rounded-[10px] px-3 text-[12px] text-[#233445] bg-white outline-none focus:border-[#2d81c1]" />
                    </div>
                    <div className="flex flex-col gap-1">
                      <label className="text-[11px] font-semibold text-[#617587]">Review Date To</label>
                      <input type="date" value={reviewDateTo} onChange={e => setReviewDateTo(e.target.value)} className="h-[38px] border border-[#e6ecf3] rounded-[10px] px-3 text-[12px] text-[#233445] bg-white outline-none focus:border-[#2d81c1]" />
                    </div>
                  </div>
                  <div className="border border-[#e6ecf3] rounded-[18px] overflow-hidden">
                    <table className="w-full border-collapse">
                      <thead>
                        <tr>
                          {["Linked Goal","Linked Action","Status","Review Date","Actions"].map(h => (
                            <th key={h} className="bg-[#fafbfd] px-[14px] py-[14px] text-left text-[12px] text-[#617587] font-semibold border-b border-[#e6ecf3]">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {!filtered.length ? (
                          <tr><td colSpan={5} className="text-center py-6 text-[13px] text-[#617587]">{reviews.length ? "No monitoring logs match the current filters." : "No monitoring logs added yet."}</td></tr>
                        ) : filtered.map((item, i) => (
                          <tr key={i} className="hover:bg-[#fcfdff]">
                            <td className="px-[14px] py-[14px] text-[13px] text-[#233445] border-b border-[#eef2f7]">{item.goal || "-"}</td>
                            <td className="px-[14px] py-[14px] text-[13px] text-[#233445] border-b border-[#eef2f7]">{item.action || "-"}</td>
                            <td className="px-[14px] py-[14px] border-b border-[#eef2f7]"><Tag cls={statusCls(item.status)}>{item.status || "-"}</Tag></td>
                            <td className="px-[14px] py-[14px] text-[13px] text-[#233445] border-b border-[#eef2f7]">{item.reviewDate || "-"}</td>
                            <td className="px-[14px] py-[14px] border-b border-[#eef2f7]">
                              <div className="flex gap-2">
                                <button onClick={() => handleEdit(reviews.indexOf(item))} className="px-3 py-1 rounded-[8px] bg-[#eef6fc] text-[#2d81c1] text-[11px] font-semibold hover:bg-[#cfe3f3]">Edit</button>
                                <button onClick={() => handleDelete(reviews.indexOf(item))} className="px-3 py-1 rounded-[8px] bg-[#fff0f0] text-[#d95353] text-[11px] font-semibold hover:bg-[#f4c5c5]">Delete</button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </Window>
              </div>
            )}

            {activeTab === "preview" && (
              <div className="grid grid-cols-2 gap-[18px]">
                <Window title="Live Monitoring Summary" note="What the current reviews are showing.">
                  {[
                    { label: "Status Snapshot", value: reviews.length ? `${onTrack} on track, ${needsAttention} needing attention, ${offTrack} off track across ${reviews.length} total monitoring entries.` : "No monitoring logs saved yet." },
                    { label: "Goal Coverage", value: uniqueGoals.length ? `Monitoring currently covers ${uniqueGoals.length} goal(s), including ${uniqueGoals.slice(0,2).join(", ")}.` : "No monitoring logs saved yet." },
                    { label: "Risk / Blocker Snapshot", value: risks ? `${risks} monitoring log(s) currently report blockers or risks that need attention.` : reviews.length ? "No blockers or risks reported yet — execution is progressing cleanly." : "No monitoring logs saved yet." },
                    { label: "Review Rhythm", value: withNextReview ? `${withNextReview} log(s) have scheduled next review dates, indicating an active review rhythm.` : "No monitoring logs saved yet." },
                  ].map((p, i) => (
                    <div key={i} className="border border-[#e6ecf3] bg-[#fbfcfe] rounded-[14px] p-4 mb-3 last:mb-0">
                      <div className="text-[11px] font-semibold text-[#8ea0b1] uppercase tracking-wider mb-2">{p.label}</div>
                      <div className="text-[13px] text-[#233445]">{p.value}</div>
                    </div>
                  ))}
                </Window>

                <Window title="Readiness Interpretation" note="How ready the school is for reporting and final compiled view.">
                  {[
                    { title: "Monitoring quality", text: interpretation },
                    { title: "Why this matters next", text: "Reports and summary views become stronger when monitoring logs clearly show status, blockers, and next checkpoints." },
                    { title: "Next connected page", text: "After this, move to Reports & Summary so the school can compile overall progress, patterns, and execution insights." },
                  ].map((info, i) => (
                    <div key={i} className="border border-[#e6ecf3] bg-[#fbfcfe] rounded-[14px] p-4 mb-3 last:mb-0">
                      <div className="text-[14px] font-semibold text-[#233445] mb-[6px]">{info.title}</div>
                      <div className="text-[12px] text-[#617587] leading-[1.7]">{info.text}</div>
                    </div>
                  ))}
                </Window>
              </div>
            )}

            <section className="bg-white rounded-xl shadow-md border border-gray-200 p-6 mt-8 mb-8">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div>
                  <h3 className="text-lg font-semibold text-[#1D3469]">Module Actions</h3>
                  <p className="text-sm text-gray-500">Save and manage module actions from one place.</p>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  <button onClick={() => { if (!reviews.length) { showToast("Add at least one log first.", "error"); return; } persist(reviews, "Draft saved"); showToast("Draft saved.", "success"); }} className="border border-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors font-medium">
                    Save Draft
                  </button>
                  <button onClick={() => { persist(reviews); showToast("Monitoring saved.", "success"); }} className="bg-[#D67C2C] hover:bg-[#c06a25] text-white px-6 py-3 rounded-lg font-medium transition-colors">
                    Save Module
                  </button>
                  <button onClick={handleFillSample} className="border border-[#D67C2C] text-[#D67C2C] px-4 py-2 rounded-lg hover:bg-[#fff4eb] transition-colors font-medium">
                    Load Sample
                  </button>
                  <button onClick={handleClear} className="border border-red-200 text-red-600 px-4 py-2 rounded-lg hover:bg-red-50 transition-colors font-medium">
                    Clear Module
                  </button>
                  <Link to="/sdp/dashboard" className="bg-[#1D3469] hover:bg-[#16284f] text-white px-6 py-3 rounded-lg font-medium transition-colors">
                    Back to Dashboard
                  </Link>
                </div>
              </div>
            </section>
          </main>
        </div>
      </div>

      <ToastContainer />
    </>
  );
}
